import { type EquipmentBrand } from '@/services/equipmentManufacturers';
import { PalmettoButton, PalmettoCheckbox, PalmettoModal } from '@palmetto/dsm-components';
import { useCallback, useState } from 'react';

interface BrandCheckboxRowProps {
  brand: EquipmentBrand;
  checked: boolean;
  onToggle: (id: string, checked: boolean) => void;
}

const BrandCheckboxRow = ({ brand, checked, onToggle }: BrandCheckboxRowProps) => {
  const handleCheckedChange = useCallback(
    (details: { checked: boolean | 'indeterminate' }) => onToggle(brand.id, details.checked === true),
    [onToggle, brand.id],
  );

  return <PalmettoCheckbox label={brand.name} checked={checked} onCheckedChange={handleCheckedChange} />;
};

export interface ActivateManufacturerModalProps {
  manufacturerName: string;
  /** Empty means there is nothing to choose between, and the modal is a plain confirmation. */
  inactiveBrands: EquipmentBrand[];
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (brandIds: string[]) => void;
}

/**
 * Nothing starts checked. A brand deliberately dropped before its manufacturer went down must not
 * come back just because the manufacturer does; Select all covers the ordinary case in one click.
 */
export const ActivateManufacturerModal = ({
  manufacturerName,
  inactiveBrands,
  isSubmitting,
  onCancel,
  onConfirm,
}: ActivateManufacturerModalProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const allSelected = inactiveBrands.length > 0 && selectedIds.length === inactiveBrands.length;

  const handleToggle = useCallback((id: string, checked: boolean) => {
    setSelectedIds((current) => (checked ? [...current, id] : current.filter((entry) => entry !== id)));
  }, []);

  const handleSelectAllChange = useCallback(
    (details: { checked: boolean | 'indeterminate' }) => {
      setSelectedIds(details.checked === true ? inactiveBrands.map((brand) => brand.id) : []);
    },
    [inactiveBrands],
  );

  const handleConfirm = useCallback(() => onConfirm(selectedIds), [onConfirm, selectedIds]);

  return (
    <PalmettoModal
      open
      onClose={onCancel}
      invertedBackground={false}
      bottomContent={
        <div className="flex justify-end gap-8">
          <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </PalmettoButton>
          <PalmettoButton
            type="button"
            emphasis="primary"
            layout="hug"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            Activate
          </PalmettoButton>
        </div>
      }
    >
      <div className="relative flex flex-col gap-16">
        <h2>Activate {manufacturerName}?</h2>
        {inactiveBrands.length === 0 ? (
          <p className="text-text-secondary">This manufacturer has no inactive brands to bring back with it.</p>
        ) : (
          <>
            <p className="text-text-secondary">
              Choose which of its {inactiveBrands.length} inactive {inactiveBrands.length === 1 ? 'brand' : 'brands'}{' '}
              to activate alongside it. Brands you leave unchecked stay inactive.
            </p>
            <div className="border-b border-border-default pb-8">
              <PalmettoCheckbox
                label="Select all"
                checked={allSelected ? true : selectedIds.length > 0 ? 'indeterminate' : false}
                onCheckedChange={handleSelectAllChange}
              />
            </div>
            <div className="flex flex-col gap-8">
              {inactiveBrands.map((brand) => (
                <BrandCheckboxRow
                  key={brand.id}
                  brand={brand}
                  checked={selectedIds.includes(brand.id)}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </PalmettoModal>
  );
};
